/**
 * Typeahead ranking used by Combobox. Pure and fast: a few thousand options
 * filter well under one frame.
 *
 * Score order: exact label match > label prefix > word prefix inside label >
 * substring in label > match in keywords/description. Every query token must
 * match somewhere, so "jo sm" finds "Smith, John".
 */
export interface Filterable {
  label: string;
  description?: string;
  keywords?: readonly string[];
}

function norm(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function tokenScore(token: string, label: string, extra: string): number {
  if (label === token) return 100;
  if (label.startsWith(token)) return 80;
  if (new RegExp(`(^|[\\s,.(/-])${escapeRe(token)}`).test(label)) return 60;
  if (label.includes(token)) return 40;
  if (extra.includes(token)) return 20;
  return 0;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function scoreOption(query: string, option: Filterable): number {
  const q = norm(query).trim();
  if (!q) return 1;
  const label = norm(option.label);
  const extra = norm([option.description ?? '', ...(option.keywords ?? [])].join(' '));
  if (label === q) return 1000;
  let total = 0;
  for (const token of q.split(/\s+/)) {
    const s = tokenScore(token, label, extra);
    if (s === 0) return 0;
    total += s;
  }
  return total;
}

/** Filters and ranks options; ties keep their original order. */
export function filterOptions<T extends Filterable>(query: string, options: readonly T[]): T[] {
  if (!query.trim()) return [...options];
  return options
    .map((option, index) => ({ option, index, score: scoreOption(query, option) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((x) => x.option);
}
